import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Grid,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import api from '../../../api/client';
import CotizacionFinalForm from './cotizacionFinalForm';

const { Title, Text, Paragraph } = Typography;

const emptyLine = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  conceptoId: null,
  conceptoNombre: '',
  detalle: '',
});

const LIMIT_LISTA = 25;

/**
 * Alineación tipo antd Select pero sin flip vertical (adjustY: false) para que el menú abra siempre hacia abajo.
 */
const selectBuiltinPlacementsBottom = {
  bottomLeft: {
    points: ['tl', 'bl'],
    offset: [0, 4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
  bottomRight: {
    points: ['tr', 'br'],
    offset: [0, 4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
  topLeft: {
    points: ['bl', 'tl'],
    offset: [0, -4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
  topRight: {
    points: ['br', 'tr'],
    offset: [0, -4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
};

/** Evita recorte por Card/overflow; fuerza apertura inferior. */
const selectPopupProps = (isNarrow) => ({
  getPopupContainer: () => document.body,
  placement: 'bottomLeft',
  listHeight: isNarrow ? 280 : undefined,
  builtinPlacements: selectBuiltinPlacementsBottom,
});

const Exportacion = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [modo, setModo] = useState('lista');

  const [loadingLista, setLoadingLista] = useState(true);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [pageLista, setPageLista] = useState(1);
  const [totalLista, setTotalLista] = useState(0);
  const [searchLista, setSearchLista] = useState('');
  const [searchListaDebounced, setSearchListaDebounced] = useState('');

  const [loadingCat, setLoadingCat] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [conceptosCatalogo, setConceptosCatalogo] = useState([]);

  const [numeroCliente, setNumeroCliente] = useState(null);
  const [productoId, setProductoId] = useState(null);
  const [lineasConcepto, setLineasConcepto] = useState([emptyLine()]);
  const [aprobada, setAprobada] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cotizacionIdEditando, setCotizacionIdEditando] = useState(null);
  const [cotizacionNroEditando, setCotizacionNroEditando] = useState(null);

  const [modalVerOpen, setModalVerOpen] = useState(false);
  const [detalleVer, setDetalleVer] = useState(null);
  const [loadingVer, setLoadingVer] = useState(false);
  const [loadingPdfVer, setLoadingPdfVer] = useState(false);

  const [cotIdLiquidacion, setCotIdLiquidacion] = useState(null);

  const clienteSeleccionado = useMemo(
    () => clientes.find((c) => c.numeroCliente === numeroCliente) || null,
    [clientes, numeroCliente]
  );

  const productoSeleccionado = useMemo(
    () => productos.find((p) => p.id === productoId) || null,
    [productos, productoId]
  );

  /** Lineas de concepto que deben verse en la grilla de resumen (catalogo con "muestra en grilla" = si) */
  const lineasConceptoParaGrilla = useMemo(() => {
    return lineasConcepto.filter((line) => {
      if (!line.conceptoId) return false;
      const c = conceptosCatalogo.find((x) => x.id === line.conceptoId);
      return c && c.muestraEnGrilla !== false;
    });
  }, [lineasConcepto, conceptosCatalogo]);

  const cargarLista = useCallback(async () => {
    setLoadingLista(true);
    try {
      const { data } = await api.get('/cotizaciones', {
        params: {
          page: pageLista,
          limit: LIMIT_LISTA,
          search: searchListaDebounced.trim() || undefined,
          tipoOperacion: 'EXPORTACION',
        },
      });
      setCotizaciones(data.items || []);
      setTotalLista(data.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar cotizaciones');
    } finally {
      setLoadingLista(false);
    }
  }, [pageLista, searchListaDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setSearchListaDebounced(searchLista.trim()), 350);
    return () => clearTimeout(t);
  }, [searchLista]);

  useEffect(() => {
    if (modo === 'lista') {
      cargarLista();
    }
  }, [modo, cargarLista]);

  const cargarCatalogos = useCallback(async () => {
    setLoadingCat(true);
    try {
      const [rCli, rProd, rCon] = await Promise.all([
        api.get('/clientes', { params: { page: 1, limit: 500 } }),
        api.get('/productos', { params: { page: 1, limit: 500 } }),
        api.get('/conceptos', { params: { page: 1, limit: 500 } }),
      ]);
      setClientes(rCli.data?.items || []);
      setProductos(rProd.data?.items || []);
      setConceptosCatalogo(rCon.data?.items || []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const abrirNuevaCotizacion = () => {
    setModo('formulario');
    setCotizacionIdEditando(null);
    setCotizacionNroEditando(null);
    setNumeroCliente(null);
    setProductoId(null);
    setLineasConcepto([emptyLine()]);
    setAprobada(false);
    setObservaciones('');
    if (!clientes.length) {
      cargarCatalogos();
    }
  };

  const abrirVer = useCallback(async (row) => {
    setModalVerOpen(true);
    setDetalleVer(null);
    setLoadingVer(true);
    try {
      const { data } = await api.get(`/cotizaciones/${row.id}`);
      setDetalleVer(data);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar la cotizacion');
      setModalVerOpen(false);
    } finally {
      setLoadingVer(false);
    }
  }, []);

  const cerrarVer = useCallback(() => {
    setModalVerOpen(false);
    setDetalleVer(null);
  }, []);

  const descargarPdfVer = useCallback(async () => {
    if (!detalleVer?.id) return;
    setLoadingPdfVer(true);
    try {
      const { data } = await api.get(`/cotizaciones/${detalleVer.id}/resumen-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const nro = detalleVer.numeroSecuencial ?? detalleVer.id;
      a.download = `cotizacion-expo-${nro}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Error al generar el PDF';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) {
          const txt = await d.text();
          const j = JSON.parse(txt);
          if (j?.message) msg = j.message;
        } else if (typeof d?.message === 'string') {
          msg = d.message;
        }
      } catch {
        /* ignore */
      }
      message.error(msg);
    } finally {
      setLoadingPdfVer(false);
    }
  }, [detalleVer]);

  const abrirLiquidacion = useCallback((row) => {
    if (!row.aprobada) return;
    setCotIdLiquidacion(row.id);
    setModo('cotizacion_final');
  }, []);

  const cerrarLiquidacion = useCallback(() => {
    setCotIdLiquidacion(null);
    setModo('lista');
    cargarLista();
  }, [cargarLista]);

  const abrirEditar = useCallback(async (row) => {
    setModo('formulario');
    setCotizacionIdEditando(row.id);
    setCotizacionNroEditando(row.numeroSecuencial ?? row.id);
    setLoadingCat(true);
    try {
      const [rCli, rProd, rCon, cotRes] = await Promise.all([
        api.get('/clientes', { params: { page: 1, limit: 500 } }),
        api.get('/productos', { params: { page: 1, limit: 500 } }),
        api.get('/conceptos', { params: { page: 1, limit: 500 } }),
        api.get(`/cotizaciones/${row.id}`),
      ]);
      setClientes(rCli.data?.items || []);
      setProductos(rProd.data?.items || []);
      setConceptosCatalogo(rCon.data?.items || []);
      const data = cotRes.data;
      setCotizacionNroEditando(data.numeroSecuencial ?? data.id);
      setNumeroCliente(data.numeroCliente);
      setProductoId(data.productoId);
      setAprobada(!!data.aprobada);
      setObservaciones(data.observaciones != null ? String(data.observaciones) : '');
      const lines = (data.conceptos || []).map((c) => ({
        key: `cotc-${c.id}`,
        conceptoId: c.conceptoId,
        conceptoNombre: c.nombre || '',
        detalle: c.detalle != null ? String(c.detalle) : '',
      }));
      setLineasConcepto(lines.length ? lines : [emptyLine()]);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar la cotizacion');
      setModo('lista');
      setCotizacionIdEditando(null);
      setCotizacionNroEditando(null);
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const volverALista = () => {
    setCotizacionIdEditando(null);
    setCotizacionNroEditando(null);
    setModo('lista');
  };

  const agregarLineaConcepto = () => {
    setLineasConcepto((prev) => [...prev, emptyLine()]);
  };

  const quitarLinea = (key) => {
    setLineasConcepto((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)));
  };

  const actualizarLinea = (key, patch) => {
    setLineasConcepto((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const onConceptoChange = (key, conceptoId) => {
    const c = conceptosCatalogo.find((x) => x.id === conceptoId);
    actualizarLinea(key, {
      conceptoId,
      conceptoNombre: c?.nombre || '',
    });
  };

  const guardarCotizacion = async () => {
    if (numeroCliente == null || productoId == null) {
      message.warning('Selecciona cliente y producto');
      return;
    }
    const lineasValidas = lineasConcepto.filter((l) => l.conceptoId);
    if (!lineasValidas.length) {
      message.warning('Agrega al menos un concepto seleccionado');
      return;
    }
    if (lineasValidas.length !== lineasConcepto.length) {
      message.warning('Completa el concepto en todas las filas o quita las vacias');
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        numeroCliente,
        productoId,
        aprobada,
        observaciones: observaciones.trim() || undefined,
        tipoOperacion: 'EXPORTACION',
        conceptos: lineasValidas.map((l) => ({
          conceptoId: l.conceptoId,
          conceptoNombre: l.conceptoNombre,
          detalle: l.detalle || '',
        })),
      };
      if (cotizacionIdEditando != null) {
        const { data } = await api.put(`/cotizaciones/${cotizacionIdEditando}`, payload);
        message.success(`Cotizacion Nº ${data.numeroSecuencial ?? data.id} actualizada`);
      } else {
        const { data } = await api.post('/cotizaciones', payload);
        message.success(`Cotizacion guardada (Nº ${data.numeroSecuencial ?? data.id})`);
      }
      setCotizacionIdEditando(null);
      setAprobada(false);
      setObservaciones('');
      setNumeroCliente(null);
      setProductoId(null);
      setLineasConcepto([emptyLine()]);
      setPageLista(1);
      setModo('lista');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar cotizacion');
    } finally {
      setGuardando(false);
    }
  };

  const datosGrilla = useMemo(() => {
    if (!clienteSeleccionado || !productoSeleccionado) {
      return [];
    }
    const row = {
      key: 'fila',
      cuit: clienteSeleccionado.cuit || '—',
      nombreCliente: clienteSeleccionado.nombre || '—',
      posicionArancelaria: productoSeleccionado.posicionArancelaria || '—',
      nombreProducto: productoSeleccionado.nombreProducto || '—',
      aprobada,
    };
    lineasConceptoParaGrilla.forEach((line) => {
      row[`concepto_${line.key}`] = line.detalle?.trim() || '—';
    });
    return [row];
  }, [clienteSeleccionado, productoSeleccionado, lineasConceptoParaGrilla, aprobada]);

  const columnasGrilla = useMemo(() => {
    const base = [
      { title: 'CUIT', dataIndex: 'cuit', width: 130, fixed: isNarrow ? 'left' : undefined },
      {
        title: 'Nombre del cliente',
        dataIndex: 'nombreCliente',
        ellipsis: true,
        minWidth: 160,
      },
      {
        title: 'Posicion arancelaria',
        dataIndex: 'posicionArancelaria',
        ellipsis: true,
        width: 160,
      },
      {
        title: 'Nombre del producto',
        dataIndex: 'nombreProducto',
        ellipsis: true,
        minWidth: 180,
      },
    ];
    const dinamicas = lineasConceptoParaGrilla.map((line) => {
      const nombre = line.conceptoNombre || 'Concepto';
      return {
        title: `${nombre}`,
        dataIndex: `concepto_${line.key}`,
        ellipsis: true,
        minWidth: 160,
        render: (text) => text || '—',
      };
    });
    const colAprobada = {
      title: 'Aprobada',
      dataIndex: 'aprobada',
      width: 100,
      align: 'center',
      render: (v) => (v ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    };
    return [...base, ...dinamicas, colAprobada];
  }, [lineasConceptoParaGrilla, isNarrow]);

  /** Nombres de conceptos (muestra en grilla) presentes en la pagina actual, ordenados por orden guardado */
  const titulosConceptosGrillaLista = useMemo(() => {
    const ordenPorNombre = new Map();
    cotizaciones.forEach((c) => {
      (c.conceptosGrilla || []).forEach((l) => {
        const nombre = l.nombre;
        if (!nombre) return;
        const orden = l.orden ?? 0;
        if (!ordenPorNombre.has(nombre)) ordenPorNombre.set(nombre, orden);
        else ordenPorNombre.set(nombre, Math.min(ordenPorNombre.get(nombre), orden));
      });
    });
    return Array.from(ordenPorNombre.entries())
      .sort((a, b) => a[1] - b[1] || String(a[0]).localeCompare(String(b[0])))
      .map(([nombre]) => nombre);
  }, [cotizaciones]);

  const columnasLista = useMemo(() => {
    const fijas = [
      {
        title: 'Nº',
        dataIndex: 'numeroSecuencial',
        width: 72,
        responsive: ['sm'],
        render: (v, row) => v ?? row.id ?? '—',
      },
      { title: 'CUIT', dataIndex: 'cuit', width: 130, ellipsis: true, render: (v) => v || '—' },
      {
        title: 'Nombre del cliente',
        dataIndex: 'clienteNombre',
        ellipsis: true,
      },
      {
        title: 'Posicion arancelaria',
        dataIndex: 'posicionArancelaria',
        ellipsis: true,
        width: 160,
      },
      {
        title: 'Nombre del producto',
        dataIndex: 'productoNombre',
        ellipsis: true,
      },
    ];
    const colsConceptos = titulosConceptosGrillaLista.map((nombreConcepto) => ({
      title: nombreConcepto,
      key: `cg-${nombreConcepto}`,
      ellipsis: true,
      minWidth: 140,
      render: (_, row) => {
        const hit = (row.conceptosGrilla || []).find((l) => l.nombre === nombreConcepto);
        const d = hit?.detalle;
        return d != null && String(d).trim() ? String(d).trim() : '—';
      },
    }));
    const colAprobada = {
      title: 'Aprobada',
      dataIndex: 'aprobada',
      width: 100,
      render: (v) => (v ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    };
    const colLiquidacion = {
      title: 'Cot. final',
      key: 'liquidacion',
      width: 130,
      align: 'center',
      render: (_, row) =>
        row.aprobada ? (
          <Button
            type="primary"
            size="small"
            ghost
            icon={<FileDoneOutlined />}
            onClick={() => abrirLiquidacion(row)}
          >
            {row.tieneCotizacionFinal ? 'Editar' : 'Cargar'}
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        ),
    };
    const colAcciones = {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 148,
      render: (_, row) => (
        <Space size={0} wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void abrirVer(row)}>
            Ver
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => void abrirEditar(row)}>
            Editar
          </Button>
        </Space>
      ),
    };
    return [...fijas, ...colsConceptos, colAprobada, colLiquidacion, colAcciones];
  }, [titulosConceptosGrillaLista, abrirVer, abrirEditar, abrirLiquidacion]);

  const scrollXLista = useMemo(
    () => Math.max(1180, 720 + titulosConceptosGrillaLista.length * 140),
    [titulosConceptosGrillaLista.length]
  );

  if (modo === 'cotizacion_final' && cotIdLiquidacion != null) {
    return (
      <CotizacionFinalForm cotId={cotIdLiquidacion} onBack={cerrarLiquidacion} onSaved={cargarLista} />
    );
  }

  if (modo === 'lista') {
    return (
      <>
      <Card
        title={
          <Title level={5} style={{ margin: 0, color: '#198039' }}>
            Exportacion — Cotizaciones
          </Title>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
        extra={
          <Space wrap size="middle" style={{ justifyContent: 'flex-end' }}>
            <Input.Search
              allowClear
              placeholder="Buscar por Nº, cliente, CUIT, producto, conceptos..."
              value={searchLista}
              onChange={(e) => {
                setSearchLista(e.target.value);
                setPageLista(1);
              }}
              style={{ width: isNarrow ? '100%' : 320 }}
            />
            <Button type="primary" onClick={abrirNuevaCotizacion}>
              Nueva cotizacion
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loadingLista}
          columns={columnasLista}
          dataSource={cotizaciones}
          pagination={{
            current: pageLista,
            pageSize: LIMIT_LISTA,
            total: totalLista,
            showSizeChanger: false,
            onChange: (p) => setPageLista(p),
            showTotal: (t) => `${t} cotizaciones`,
            size: isNarrow ? 'small' : 'default',
            simple: isNarrow,
          }}
          scroll={{ x: scrollXLista }}
          size="small"
        />
      </Card>

      <Modal
        title={detalleVer ? `Cotizacion Nº ${detalleVer.numeroSecuencial ?? detalleVer.id}` : 'Detalle'}
        open={modalVerOpen}
        onCancel={cerrarVer}
        footer={[
          <Button
            key="pdf"
            icon={<FilePdfOutlined />}
            loading={loadingPdfVer}
            disabled={!detalleVer || loadingVer}
            onClick={() => void descargarPdfVer()}
          >
            PDF
          </Button>,
          <Button key="cerrar" type="primary" onClick={cerrarVer}>
            Cerrar
          </Button>,
        ]}
        width={isNarrow ? 'calc(100vw - 24px)' : 560}
        destroyOnClose
      >
        <Spin spinning={loadingVer}>
          {detalleVer && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Cliente
                </Text>
                <div>
                  {detalleVer.clienteNombre || '—'}
                  {detalleVer.cuit ? ` · CUIT ${detalleVer.cuit}` : ''}
                </div>
                <Text type="secondary">Nº cliente: {detalleVer.numeroCliente ?? '—'}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Producto
                </Text>
                <div>{detalleVer.productoNombre || '—'}</div>
                {detalleVer.posicionArancelaria ? (
                  <Text type="secondary">Posicion arancelaria: {detalleVer.posicionArancelaria}</Text>
                ) : null}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Observaciones
                </Text>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {detalleVer.observaciones != null && String(detalleVer.observaciones).trim()
                    ? detalleVer.observaciones
                    : '—'}
                </Paragraph>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ marginRight: 8 }}>
                  Aprobada
                </Text>
                {detalleVer.aprobada ? <Tag color="green">Si</Tag> : <Tag>No</Tag>}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Conceptos
                </Text>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {(detalleVer.conceptos || []).length === 0 ? (
                    <Text type="secondary">Sin conceptos</Text>
                  ) : (
                    (detalleVer.conceptos || []).map((c) => (
                      <div key={c.id}>
                        <Text strong>{c.nombre || 'Concepto'}</Text>
                        <Paragraph style={{ marginBottom: 0, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                          {c.detalle != null && String(c.detalle).trim() ? c.detalle : '—'}
                        </Paragraph>
                      </div>
                    ))
                  )}
                </Space>
              </div>
            </Space>
          )}
        </Spin>
      </Modal>
      </>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={volverALista}>
              Volver
            </Button>
            <Title level={5} style={{ margin: 0, color: '#198039' }}>
              {cotizacionIdEditando != null
                ? `Editar cotizacion Nº ${cotizacionNroEditando ?? cotizacionIdEditando}`
                : 'Nueva cotizacion'}
            </Title>
          </Space>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
        loading={loadingCat}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Elegi cliente y producto. Luego agrega los conceptos que necesites y el detalle de cada uno.
        </Text>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} md={12} lg={11}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 6 }}>
                    Cliente
                  </Text>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Seleccionar cliente"
                    style={{ width: '100%' }}
                    optionFilterProp="label"
                    value={numeroCliente ?? undefined}
                    onChange={(v) => setNumeroCliente(v ?? null)}
                    options={clientes.map((c) => ({
                      value: c.numeroCliente,
                      label: `${c.nombre} (Nº ${c.numeroCliente})`,
                    }))}
                    {...selectPopupProps(isNarrow)}
                  />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 6 }}>
                    Producto
                  </Text>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Seleccionar producto"
                    style={{ width: '100%' }}
                    optionFilterProp="label"
                    value={productoId ?? undefined}
                    onChange={(v) => setProductoId(v ?? null)}
                    options={productos.map((p) => ({
                      value: p.id,
                      label: `${p.nombreProducto}${p.posicionArancelaria ? ` — ${p.posicionArancelaria}` : ''}`,
                    }))}
                    {...selectPopupProps(isNarrow)}
                  />
                </div>
              </Space>
            </Col>
            <Col xs={24} md={12} lg={13}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>
                Observaciones
              </Text>
              <Input.TextArea
                rows={isNarrow ? 2 : 5}
                placeholder="Notas u observaciones sobre esta cotización..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </Col>
          </Row>

          <div>
            <Space align="center" wrap>
              <Text strong>Cotizacion aprobada</Text>
              <Switch checked={aprobada} onChange={setAprobada} checkedChildren="Si" unCheckedChildren="No" />
            </Space>
          </div>

          <div>
            <Space align="center" style={{ marginBottom: 8 }}>
              <Text strong>Conceptos</Text>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={agregarLineaConcepto}>
                Agregar concepto
              </Button>
            </Space>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {lineasConcepto.map((line, index) => (
                <Card
                  key={line.key}
                  size="small"
                  type="inner"
                  styles={{ body: { padding: isNarrow ? 10 : 12 } }}
                  style={{ borderColor: '#d8e8dc' }}
                >
                  <Space
                    direction={isNarrow ? 'vertical' : 'horizontal'}
                    style={{ width: '100%' }}
                    align={isNarrow ? 'stretch' : 'start'}
                    wrap
                  >
                    <div
                      style={{
                        flex: isNarrow ? 1 : '0 0 min(100%, 520px)',
                        minWidth: isNarrow ? 0 : 400,
                        width: '100%',
                        maxWidth: '100%',
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                        Concepto {index + 1}
                      </Text>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Elegir concepto"
                        style={{ width: '100%', minWidth: isNarrow ? undefined : 400 }}
                        optionFilterProp="label"
                        popupMatchSelectWidth={isNarrow}
                        dropdownStyle={
                          isNarrow
                            ? { maxWidth: 'calc(100vw - 24px)', minWidth: 0 }
                            : { minWidth: 480, maxWidth: 720 }
                        }
                        value={line.conceptoId ?? undefined}
                        onChange={(id) => onConceptoChange(line.key, id)}
                        options={conceptosCatalogo.map((c) => ({
                          value: c.id,
                          label: c.nombre,
                        }))}
                        {...selectPopupProps(isNarrow)}
                      />
                    </div>
                    <div
                      style={{
                        flex: isNarrow ? 1 : '1 1 480px',
                        minWidth: isNarrow ? 0 : 420,
                        width: '100%',
                        maxWidth: '100%',
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                        Detalle / lo que cuenta
                      </Text>
                      <Input.TextArea
                        rows={2}
                        placeholder="Ej: Honorarios despachante, importes, observaciones..."
                        value={line.detalle}
                        onChange={(e) => actualizarLinea(line.key, { detalle: e.target.value })}
                        style={{ width: '100%', minWidth: isNarrow ? undefined : 420 }}
                      />
                    </div>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => quitarLinea(line.key)}
                      disabled={lineasConcepto.length <= 1}
                      aria-label="Quitar concepto"
                    />
                  </Space>
                </Card>
              ))}
            </Space>
          </div>

          <div>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={guardando}
              onClick={guardarCotizacion}
              size="large"
            >
              {cotizacionIdEditando != null ? 'Guardar cambios' : 'Guardar cotizacion'}
            </Button>
          </div>
        </Space>
      </Card>

      <Card
        title="Resumen"
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 8 : 12 } }}
      >
        {!clienteSeleccionado || !productoSeleccionado ? (
          <Text type="secondary">Selecciona cliente y producto para ver la grilla.</Text>
        ) : (
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={columnasGrilla}
            dataSource={datosGrilla}
            rowKey="key"
          />
        )}
      </Card>
    </Space>
  );
};

export default Exportacion;
